import { useEffect, useCallback, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { tabbable } from 'tabbable'

import useEventListener from '../../../../hooks/useEventListener'
import useScrollLockToggle from '../../../../hooks/useScrollLockToggle'
import useWindowSize from '../../../../hooks/useWindowSize'

import { getCssVar } from '../../../../utils/css'

export default function DashboardHubTopicsCard (props = {
    expandable: false,
    searchPathname: null,
    topics: null
}) {
    const {
        expandable,
        searchPathname,
        topics
    } = props

    const topicsRef = useRef()

    const history = useHistory()

    const size = useWindowSize()

    const [expanded, setExpanded] = useState()

    const [scrollLock, toggleScrollLock] = useScrollLockToggle()

    useEffect(() => {
        if (expanded)
            setExpanded(false)
    }, [history.location.pathname])

    useEffect(() => {
        if (expanded
                && window.innerWidth >= 1024)
            setExpanded(false)
    }, [size])

    // Tab trap
    useEventListener('keydown', useCallback((e) => {
        if (!(scrollLock
                && expanded
                && window.innerWidth < 1024))
            return

        // On Escape
        if (e.key.match(/escape/i))
            setExpanded(false)

        // Trap Tab keypresses
        if (!e.key.match(/tab/i)
                || !topicsRef.current)
            return

        let activeEl

        const els = tabbable(topicsRef.current) || []

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            activeEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            activeEl = els[0]

        if (!activeEl)
            return

        e.preventDefault()
        e.stopPropagation()

        activeEl.focus()
    }, [scrollLock]))

    useEffect(() => {
        if (!expanded)
            return typeof expanded === 'boolean'
                && toggleScrollLock()

        toggleScrollLock()
    }, [expanded])

    if (!topics || !topics.length)
        return null

    const renderCard = () => (
        <div className="card">
            {!expandable
                && (
                    <h2 className="body-3 margin-3-b">
                        <span className="font-weight-medium display">
                            Explore our topics
                        </span>
                    </h2>)}
            {expandable
                && (
                    <button
                        id="dashboard-hub-topics-expandable-toggle"
                        className="dashboard-hub-topics-expandable-toggle btn-unstyled"
                        type="button"
                        aria-controls="dashboard-hub-topics-expandable-content"
                        aria-expanded={Boolean(expanded)}
                        aria-label={expanded
                            ? 'Hide topics'
                            : 'Show topics'}
                        onClick={() => setExpanded(Boolean(!expanded))}
                        style={{ color: getCssVar('--navy') }}
                    >
                        <span className="body-3 font-weight-medium display">
                            Explore our topics
                        </span>
                    </button>
                )}
            <div
                id={expandable
                    ? 'dashboard-hub-topics-expandable-content'
                    : null}
                className="dashboard-hub-topics-content"
            >
                <ul
                    className={`display-flex flex-wrap ${!expandable
                        ? 'margin-1-b-neg'
                        : ''}`}
                    style={{
                        paddingBottom: expandable
                            ? 15
                            : null
                    }}
                >
                    {topics
                        .map((obj, idx) => (
                            <li
                                key={`dashboard-hub-landing-topics-${idx}`}
                                className="margin-1-b margin-1-r col-auto pad-0"
                            >
                                <Link
                                    to={`${searchPathname}?topic=${obj.slug}`}
                                    className="body-4 display-flex flex-nowrap flex-justify-content-center text-decoration-hover"
                                >
                                    {obj.icon
                                        && (
                                            <svg
                                                className={obj.icon}
                                                width="18"
                                                height="18"
                                                style={{ marginRight: 5 }}
                                            >
                                                <use xlinkHref={`#${obj.icon}`} />
                                            </svg>
                                        )}
                                    <span>{obj.title}</span>
                                </Link>
                            </li>
                        ))}
                </ul>
            </div>
        </div>
    )

    return (
        <section
            ref={topicsRef}
            className={`dashboard-hub-topics${
                expandable
                    ? ' dashboard-hub-topics-expandable'
                    : ''}`}
            data-expanded={expandable
                ? `${expanded}`
                : null}
        >
            {!expandable
                && renderCard()}
            {expandable
                && (
                    <>
                        <div
                            className="dashboard-hub-topics-expandable-bg"
                            onClick={() => setExpanded(false)}
                        />
                        <div
                            className="dashboard-hub-topics-expandable-dialog"
                            aria-label={expandable
                                    && expanded
                                ? 'Topics'
                                : null}
                            aria-modal={expandable
                                    && expanded
                                ? 'dialog'
                                : null}
                            role={expandable
                                    && expanded
                                ? 'dialog'
                                : null}
                        >
                            {renderCard()}
                        </div>
                    </>
                )}
        </section>
    )
}
